import { maxPerPage } from "@/constants";
import { buildQueryParams, cleanParams } from "@/utils";

/**
 * Получает список категорий НСПД.
 * @returns {Promise<Object>} - Данные.
 * @throws {Error} - Ошибка при получении данных.
 */
export const getKeyCategories = async (params) => {
  const cleanedFilters = cleanParams(params);
  const queryString = buildQueryParams(cleanedFilters);

  try {
    const response = await fetch(`/api/nspd/categories/admin?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получает список категорий ключей НСПД для селектов без ограничения первой страницы.
 */
export const getKeyCategoriesOptions = async (params) => {
  const cleanedFilters = cleanParams({
    ...params,
    page: 1,
    perpage: maxPerPage,
  });
  const queryString = buildQueryParams(cleanedFilters);

  try {
    const response = await fetch(`/api/nspd/categories/admin?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data.data || [];
  } catch (err) {
    throw err;
  }
};

/**
 *  Создание категории НСПД
 * @param {object} item - данные
 * @returns {Promise} - созданная категория
 * @throws {Error} - ошибка при создании данных
 */
export const createKeyCategory = async (item) => {
  try {
    const response = await fetch(`/api/nspd/category/admin`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновление категории НСПД
 * @param {object} item - данные
 * @returns {Promise} - обновленная категория
 * @throws {Error} - ошибка при обновлении данных
 */
export const updateKeyCategory = async (item) => {
  try {
    const response = await fetch(`/api/nspd/category/admin`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление категории
 * @param {object} item - данные
 * @returns {Promise} - удаленная категория
 * @throws {Error} - ошибка при удалении данных
 */
export const deleteKeyCategory = async (item) => {
  try {
    const response = await fetch(`/api/nspd/category/admin`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получает список ключей НСПД.
 * @returns {Promise<Object>} - Данные.
 * @throws {Error} - Ошибка при получении данных.
 */
export const getKeys = async (params) => {
  const cleanedFilters = cleanParams(params);
  const queryString = buildQueryParams(cleanedFilters);

  try {
    const response = await fetch(`/api/nspd/keys/admin?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получает список ключей НСПД для селектов без ограничения первой страницы.
 */
export const getKeysOptions = async (params) => {
  const cleanedFilters = cleanParams({
    ...params,
    page: 1,
    perpage: maxPerPage,
  });
  const queryString = buildQueryParams(cleanedFilters);

  try {
    const response = await fetch(`/api/nspd/keys/admin?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data.data || [];
  } catch (err) {
    throw err;
  }
};

/**
 *  Создание ключа НСПД
 * @param {object} item - данные
 * @returns {Promise} - созданный ключ
 * @throws {Error} - ошибка при создании данных
 */
export const createKey = async (item) => {
  try {
    const response = await fetch(`/api/nspd/key/admin`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновление ключа НСПД
 * @param {object} item - данные
 * @returns {Promise} - обновленный ключ
 * @throws {Error} - ошибка при обновлении данных
 */
export const updateKey = async (item) => {
  try {
    const response = await fetch(`/api/nspd/key/admin`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление ключа
 * @param {object} item - данные
 * @returns {Promise} - удаленный ключ
 * @throws {Error} - ошибка при удалении данных
 */
export const deleteKey = async (item) => {
  try {
    const response = await fetch(`/api/nspd/key/admin`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Создание связи ключ - категория
 * @param {object} item - данные
 * @returns {Promise} - обновленная связь
 * @throws {Error} - ошибка при обновлении данных
 */
export const createKeyCategoryRelationship = async (item) => {
  try {
    const response = await fetch(`/api/nspd/category-key/admin`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление связи ключ - категория
 * @param {object} item - данные
 * @returns {Promise} - удаленная связь
 * @throws {Error} - ошибка при удалении данных
 */
export const deleteKeyCategoryRelationship = async (item) => {
  try {
    const response = await fetch(`/api/nspd/category-key/admin`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получает JSON.
 * @returns {Promise<Object>} - Данные.
 * @throws {Error} - Ошибка при получении данных.
 */
export const getNSPDRawJson = async (number, isAdmin = false) => {
  const response = await fetch(`/api/nspd/raw-data${isAdmin ? '/admin' : ""}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ query: number }),
  });

  const data = await response.json();

  if (!response.ok || data.error) {
    const errorMessage =
      data?.error?.message || data?.message || "Обновите страницу";

    throw new Error(`Ошибка при получении данных. ${errorMessage}`);
  }

  return data.data;
};
